// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Master Data Page (Plants, Tanks, Warehouses)
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/Tabs';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Plus, Search, Edit, Trash2, Building2, Warehouse, Droplets } from 'lucide-react';
import { useApi } from '@/shared/hooks/useApi';

interface Plant {
  _id: string;
  code: string;
  name: string;
  status: string;
  units: any[];
  tankIds: string[];
}

interface Tank {
  _id: string;
  code: string;
  name: string;
  type: string;
  maxVolume: number;
  currentVolume: number;
  currentMass: number;
  currentTemp: number;
  currentApiGravity: number;
  status: string;
  productName: string;
  batchNumber: string;
}

interface Warehouse {
  _id: string;
  code: string;
  name: string;
  type: string;
  bins: any[];
  status: string;
}

export function MasterDataPage() {
  const [activeTab, setActiveTab] = useState<'plants' | 'tanks' | 'warehouses'>('plants');
  const [plants, setPlants] = useState<Plant[]>([]);
  const [tanks, setTanks] = useState<Tank[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const { get, post, put, del } = useApi();
  
  // Dialog states
  const [showPlantDialog, setShowPlantDialog] = useState(false);
  const [showTankDialog, setShowTankDialog] = useState(false);
  const [showWarehouseDialog, setShowWarehouseDialog] = useState(false);
  const [editingPlant, setEditingPlant] = useState<Plant | null>(null);
  const [editingTank, setEditingTank] = useState<Tank | null>(null);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [formData, setFormData] = useState<any>({});
  
  useEffect(() => {
    fetchData();
  }, [activeTab, get]);
  
  async function fetchData() {
    setLoading(true);
    try {
      // Always fetch plants so plant dropdowns are always populated in Tank & Warehouse modals
      const plantsRes = await get('/api/manufacturing/master/plants');
      setPlants(plantsRes.plants || []);

      if (activeTab === 'tanks') {
        const res = await get('/api/manufacturing/master/tanks');
        setTanks(res.tanks || []);
      } else if (activeTab === 'warehouses') {
        const res = await get('/api/manufacturing/master/warehouses');
        setWarehouses(res.warehouses || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }
  
  function handleOpenCreate(type: 'plants' | 'tanks' | 'warehouses') {
    if (type === 'plants') {
      setEditingPlant(null);
      setFormData({ code: '', name: '', description: '', status: 'active', address: { line1: '', city: '', state: '', country: 'India' }, units: [] });
      setShowPlantDialog(true);
    } else if (type === 'tanks') {
      setEditingTank(null);
      setFormData({ code: '', name: '', type: 'crude_oil', status: 'active', maxVolume: 1000000, plantId: plants[0]?._id || '', safeFillPercent: 90 });
      setShowTankDialog(true);
    } else {
      setEditingWarehouse(null);
      setFormData({ code: '', name: '', type: 'finished_goods', status: 'active', plantId: plants[0]?._id || '', bins: [] });
      setShowWarehouseDialog(true);
    }
  }
  
  function handleEdit(item: any, type: 'plants' | 'tanks' | 'warehouses') {
    if (type === 'plants') {
      setEditingPlant(item);
      setFormData({ ...item });
      setShowPlantDialog(true);
    } else if (type === 'tanks') {
      setEditingTank(item);
      setFormData({ ...item });
      setShowTankDialog(true);
    } else {
      setEditingWarehouse(item);
      setFormData({ ...item });
      setShowWarehouseDialog(true);
    }
  }
  
  async function handlePlantSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingPlant) {
        await put(`/api/manufacturing/master/plants/${editingPlant._id}`, formData);
      } else {
        await post('/api/manufacturing/master/plants', formData);
      }
      setShowPlantDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save plant:', error);
    }
  }

  async function handleTankSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingTank) {
        await put(`/api/manufacturing/master/tanks/${editingTank._id}`, formData);
      } else {
        await post('/api/manufacturing/master/tanks', formData);
      }
      setShowTankDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save tank:', error);
    }
  }

  async function handleWarehouseSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingWarehouse) {
        await put(`/api/manufacturing/master/warehouses/${editingWarehouse._id}`, formData);
      } else {
        await post('/api/manufacturing/master/warehouses', formData);
      }
      setShowWarehouseDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save warehouse:', error);
    }
  }
  
  function handleDelete(id: string) {
    if (confirm('Are you sure you want to delete this item?')) {
      del(`/api/manufacturing/master/${activeTab}/${id}`).then(fetchData);
    }
  }
  
  const filteredPlants = plants.filter(p => 
    p.code.toLowerCase().includes(search.toLowerCase()) ||
    p.name.toLowerCase().includes(search.toLowerCase())
  ).filter(p => !filterStatus || p.status === filterStatus);
  
  const filteredTanks = tanks.filter(t => 
    t.code.toLowerCase().includes(search.toLowerCase()) ||
    t.name.toLowerCase().includes(search.toLowerCase())
  ).filter(t => !filterStatus || t.status === filterStatus);

  const filteredWarehouses = warehouses.filter(w => 
    w.code.toLowerCase().includes(search.toLowerCase()) ||
    w.name.toLowerCase().includes(search.toLowerCase())
  ).filter(w => !filterStatus || w.status === filterStatus);
  
  const fillPercent = (tank: Tank) => tank.maxVolume > 0 ? ((tank.currentVolume || 0) / tank.maxVolume) * 100 : 0;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Plants & Tanks Master</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage refineries, distillation units, tanks, warehouses, and dip charts</p>
        </div>
        <Button onClick={() => handleOpenCreate(activeTab)}>
          <Plus className="w-4 h-4 mr-2" />
          Add {activeTab.slice(0, -1).charAt(0).toUpperCase() + activeTab.slice(1, -1)}
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="plants"><Building2 className="w-4 h-4 mr-2" /> Plants</TabsTrigger>
          <TabsTrigger value="tanks"><Droplets className="w-4 h-4 mr-2" /> Tanks</TabsTrigger>
          <TabsTrigger value="warehouses"><Warehouse className="w-4 h-4 mr-2" /> Warehouses</TabsTrigger>
        </TabsList>
        
        {/* Plants Tab */}
        <TabsContent value="plants" className="space-y-4">
          <div className="flex gap-4">
            <Input placeholder="Search plants..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="under_construction">Under Construction</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {loading ? (
            <Card className="p-8 text-center">Loading...</Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Units</TableHead>
                    <TableHead>Tanks</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPlants.map(plant => (
                    <TableRow key={plant._id}>
                      <TableCell className="font-mono font-medium">{plant.code}</TableCell>
                      <TableCell>{plant.name}</TableCell>
                      <TableCell>{plant.units?.length || 0} units</TableCell>
                      <TableCell>{plant.tankIds?.length || 0} tanks</TableCell>
                      <TableCell><Badge variant={plant.status === 'active' ? 'success' : 'secondary'}>{plant.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(plant, 'plants')}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(plant._id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredPlants.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">No plants found. Click "Add Plant" to create one.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
        
        {/* Tanks Tab */}
        <TabsContent value="tanks" className="space-y-4">
          <div className="flex gap-4">
            <Input placeholder="Search tanks..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="cleaning">Cleaning</SelectItem>
                <SelectItem value="quarantine">Quarantine</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Types</SelectItem>
                <SelectItem value="crude_oil">Crude Oil</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="finished_product">Finished Product</SelectItem>
                <SelectItem value="by_product">By-Product</SelectItem>
                <SelectItem value="slop">Slop</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {loading ? (
            <Card className="p-8 text-center">Loading...</Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="w-32">Fill Level</TableHead>
                    <TableHead>Volume (L)</TableHead>
                    <TableHead>Mass (kg)</TableHead>
                    <TableHead>Temp (°C)</TableHead>
                    <TableHead>API</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTanks.map(tank => (
                    <TableRow key={tank._id}>
                      <TableCell className="font-mono font-medium">{tank.code}</TableCell>
                      <TableCell>{tank.name}</TableCell>
                      <TableCell><Badge variant="secondary">{tank.type}</Badge></TableCell>
                      <TableCell>
                        <div className="w-32">
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.min(100, fillPercent(tank))}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{fillPercent(tank).toFixed(1)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{(tank.currentVolume || 0).toLocaleString()}</TableCell>
                      <TableCell>{(tank.currentMass || 0).toLocaleString()}</TableCell>
                      <TableCell>{tank.currentTemp || 15}</TableCell>
                      <TableCell>{tank.currentApiGravity ? `${tank.currentApiGravity}°API` : '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{tank.productName || '-'}</TableCell>
                      <TableCell><Badge variant={tank.status === 'active' ? 'success' : tank.status === 'quarantine' ? 'destructive' : 'secondary'}>{tank.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(tank, 'tanks')}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(tank._id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTanks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-gray-500">No tanks found. Click "Add Tank" to create one.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
        
        {/* Warehouses Tab */}
        <TabsContent value="warehouses" className="space-y-4">
          <div className="flex gap-4">
            <Input placeholder="Search warehouses..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {loading ? (
            <Card className="p-8 text-center">Loading...</Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Bins</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWarehouses.map(wh => (
                    <TableRow key={wh._id}>
                      <TableCell className="font-mono font-medium">{wh.code}</TableCell>
                      <TableCell>{wh.name}</TableCell>
                      <TableCell><Badge variant="secondary">{wh.type}</Badge></TableCell>
                      <TableCell>{wh.bins?.length || 0}</TableCell>
                      <TableCell><Badge variant={wh.status === 'active' ? 'success' : 'secondary'}>{wh.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(wh, 'warehouses')}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(wh._id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredWarehouses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">No warehouses found. Click "Add Warehouse" to create one.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Create/Edit Dialogs */}
      <Dialog open={showPlantDialog} onOpenChange={setShowPlantDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlant ? 'Edit Plant' : 'Create Plant'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePlantSubmit}>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Code *</label>
                  <Input value={formData.code || ''} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Input value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <Input value={formData.address?.city || ''} onChange={e => setFormData({...formData, address: {...formData.address, city: e.target.value}})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State</label>
                  <Input value={formData.address?.state || ''} onChange={e => setFormData({...formData, address: {...formData.address, state: e.target.value}})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <Input value={formData.address?.country || 'India'} onChange={e => setFormData({...formData, address: {...formData.address, country: e.target.value}})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status *</label>
                  <Select 
                    value={formData.status || 'active'} 
                    onValueChange={v => setFormData({...formData, status: v})}
                    options={[
                      { label: 'Active', value: 'active' },
                      { label: 'Inactive', value: 'inactive' },
                      { label: 'Maintenance', value: 'maintenance' },
                      { label: 'Decommissioned', value: 'decommissioned' },
                    ]}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPlantDialog(false)}>Cancel</Button>
              <Button type="submit">{editingPlant ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showTankDialog} onOpenChange={setShowTankDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTank ? 'Edit Tank' : 'Create Tank'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTankSubmit}>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Code *</label>
                  <Input value={formData.code || ''} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} required />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Type *</label>
                  <Select 
                    value={formData.type || 'crude_oil'} 
                    onValueChange={v => setFormData({...formData, type: v})}
                    options={[
                      { label: 'Crude Oil', value: 'crude_oil' },
                      { label: 'Intermediate', value: 'intermediate' },
                      { label: 'Finished Product', value: 'finished_product' },
                      { label: 'By-Product', value: 'by_product' },
                      { label: 'Slop', value: 'slop' },
                      { label: 'Water', value: 'water' },
                      { label: 'Chemical', value: 'chemical' },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plant</label>
                  <Select 
                    value={formData.plantId || ''} 
                    onValueChange={v => setFormData({...formData, plantId: v})}
                    placeholder="Select Plant"
                    options={[
                      { label: 'Default Refinery', value: '' },
                      ...plants.map(p => ({ label: `${p.code} - ${p.name}`, value: p._id }))
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status *</label>
                  <Select 
                    value={formData.status || 'active'} 
                    onValueChange={v => setFormData({...formData, status: v})}
                    options={[
                      { label: 'Active', value: 'active' },
                      { label: 'Maintenance', value: 'maintenance' },
                      { label: 'Cleaning', value: 'cleaning' },
                      { label: 'Quarantine', value: 'quarantine' },
                      { label: 'Decommissioned', value: 'decommissioned' },
                    ]}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Max Volume (L) *</label>
                  <Input type="number" value={formData.maxVolume || ''} onChange={e => setFormData({...formData, maxVolume: Number(e.target.value)})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Safe Fill %</label>
                  <Input type="number" value={formData.safeFillPercent || ''} onChange={e => setFormData({...formData, safeFillPercent: Number(e.target.value)})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Operating Volume (L)</label>
                  <Input type="number" value={formData.minOperatingVolume || ''} onChange={e => setFormData({...formData, minOperatingVolume: Number(e.target.value)})} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTankDialog(false)}>Cancel</Button>
              <Button type="submit">{editingTank ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showWarehouseDialog} onOpenChange={setShowWarehouseDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWarehouse ? 'Edit Warehouse' : 'Create Warehouse'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleWarehouseSubmit}>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Code *</label>
                  <Input value={formData.code || ''} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} required />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Type *</label>
                  <Select 
                    value={formData.type || 'finished_goods'} 
                    onValueChange={v => setFormData({...formData, type: v})}
                    options={[
                      { label: 'Raw Material', value: 'raw_material' },
                      { label: 'WIP', value: 'wip' },
                      { label: 'Finished Goods', value: 'finished_goods' },
                      { label: 'By-Product', value: 'by_product' },
                      { label: 'Waste', value: 'waste' },
                      { label: 'Packaging Material', value: 'packaging_material' },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plant</label>
                  <Select 
                    value={formData.plantId || ''} 
                    onValueChange={v => setFormData({...formData, plantId: v})}
                    placeholder="Select Plant"
                    options={[
                      { label: 'Default Refinery', value: '' },
                      ...plants.map(p => ({ label: `${p.code} - ${p.name}`, value: p._id }))
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status *</label>
                  <Select 
                    value={formData.status || 'active'} 
                    onValueChange={v => setFormData({...formData, status: v})}
                    options={[
                      { label: 'Active', value: 'active' },
                      { label: 'Inactive', value: 'inactive' },
                      { label: 'Maintenance', value: 'maintenance' },
                      { label: 'Full', value: 'full' },
                      { label: 'Blocked', value: 'blocked' },
                    ]}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowWarehouseDialog(false)}>Cancel</Button>
              <Button type="submit">{editingWarehouse ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}